import { nanoid } from 'nanoid';
import { useMemo, useState } from 'react';

import { usePerson } from '@/store/persons/person.store';

import type {
  CrewRosterEntry,
  DailyLogFormState,
  DayWorkerRow,
  DrillEntry,
  NarrativeEntry,
  ShoreConnectionKey,
  TankLevels,
  YNValue,
} from '../types';

const getTodayISO = () => new Date().toISOString();

const INITIAL_STATE: DailyLogFormState = {
  location: '',
  country: '',
  date: getTodayISO(),
  sternTo: false,
  alongside: false,
  anchored: false,
  watchkeeperIds: [],
  crewRoster: [],
  narrativeEntries: [],
  drillEntries: [],
  shoreConnections: { power: null, water: null, phone: null, internet: null },
  tankLevels: { fuel: '', water: '', greyWater: '', blackWater: '' },
  usageDays: '',
  underwayDays: '',
  ownerGuestMeals: '',
  dayWorkers: [],
  captainSignature: { personId: null, signedAt: null },
  officerSignature: { personId: null, signedAt: null },
};

export function useDailyLog() {
  const [formState, setFormState] = useState<DailyLogFormState>(INITIAL_STATE);
  const currentUser = usePerson((s) => s.currentUser);
  const currentUserInitials = currentUser
    ? `${currentUser.person_info.first_name[0] ?? ''}.${currentUser.person_info.last_name[0] ?? ''}.`
    : '';

  const addedCrewIds = useMemo(
    () => new Set(formState.crewRoster.map((e) => e.userId)),
    [formState.crewRoster],
  );

  const addedWatchkeeperIds = useMemo(
    () => new Set(formState.watchkeeperIds),
    [formState.watchkeeperIds],
  );

  const setField = <K extends keyof DailyLogFormState>(field: K, value: DailyLogFormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  function makeListHandlers<T extends { id: string }>(
    key: keyof DailyLogFormState,
    makeItem: () => T,
  ) {
    const items = (state: DailyLogFormState) => state[key] as unknown as T[];
    return {
      add: () => setFormState((prev) => ({ ...prev, [key]: [...items(prev), makeItem()] })),
      remove: (id: string) =>
        setFormState((prev) => ({ ...prev, [key]: items(prev).filter((e) => e.id !== id) })),
      update: (id: string, patch: Partial<T>) =>
        setFormState((prev) => ({
          ...prev,
          [key]: items(prev).map((e) => (e.id === id ? { ...e, ...patch } : e)),
        })),
    };
  }

  const {
    add: addNarrativeEntry,
    remove: removeNarrativeEntry,
    update: updateNarrativeEntry,
  } = makeListHandlers<NarrativeEntry>('narrativeEntries', () => ({
    id: nanoid(),
    text: '',
    initials: currentUserInitials,
  }));

  const {
    add: addDrillEntry,
    remove: removeDrillEntry,
    update: updateDrillEntry,
  } = makeListHandlers<DrillEntry>('drillEntries', () => ({
    id: nanoid(),
    text: '',
    initials: currentUserInitials,
  }));

  const {
    add: addDayWorker,
    remove: removeDayWorker,
    update: updateDayWorker,
  } = makeListHandlers<DayWorkerRow>('dayWorkers', () => ({
    id: nanoid(),
    name: '',
    contact: '',
    dailyRate: '',
    email: '',
  }));

  // Crew roster uses userId as the key (not id), so handled separately
  const addCrewMember = (user: CrewRosterEntry) =>
    setFormState((prev) => ({ ...prev, crewRoster: [...prev.crewRoster, user] }));

  const removeCrewMember = (userId: string) =>
    setFormState((prev) => ({
      ...prev,
      crewRoster: prev.crewRoster.filter((e) => e.userId !== userId),
    }));

  const updateCrewEntry = (userId: string, patch: Partial<CrewRosterEntry>) =>
    setFormState((prev) => ({
      ...prev,
      crewRoster: prev.crewRoster.map((e) => (e.userId === userId ? { ...e, ...patch } : e)),
    }));

  const setShoreConnection = (key: ShoreConnectionKey, val: YNValue) =>
    setFormState((prev) => ({
      ...prev,
      shoreConnections: { ...prev.shoreConnections, [key]: val },
    }));

  const setTankLevel = (key: keyof TankLevels, val: string) =>
    setFormState((prev) => ({
      ...prev,
      tankLevels: { ...prev.tankLevels, [key]: val },
    }));

  const signCaptain = (personId: string) =>
    setFormState((prev) => ({
      ...prev,
      captainSignature: { personId, signedAt: new Date().toISOString() },
    }));

  const signOfficer = (personId: string) =>
    setFormState((prev) => ({
      ...prev,
      officerSignature: { personId, signedAt: new Date().toISOString() },
    }));

  const assignOfficer = (personId: string | null) =>
    setFormState((prev) => ({
      ...prev,
      officerSignature: { personId, signedAt: null },
    }));

  const prefillForm = (state: DailyLogFormState) => setFormState(state);

  const resetForm = (date: string) => setFormState({ ...INITIAL_STATE, date });

  return {
    formState,
    addedCrewIds,
    addedWatchkeeperIds,
    setField,
    prefillForm,
    resetForm,
    addCrewMember,
    removeCrewMember,
    updateCrewEntry,
    addNarrativeEntry,
    removeNarrativeEntry,
    updateNarrativeEntry,
    addDrillEntry,
    removeDrillEntry,
    updateDrillEntry,
    setShoreConnection,
    setTankLevel,
    addDayWorker,
    removeDayWorker,
    updateDayWorker,
    signCaptain,
    signOfficer,
    assignOfficer,
  };
}
